import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/api";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { useDispatch } from "react-redux";
import { addloginData } from "../../redux/action";
import { LogIn, Lock, User, Loader2, Mail, Phone, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
    const dispatch=useDispatch()
  const navigate = useNavigate();
  const [contact, setContact] = useState(""); // Can be email or mobile
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError("Password is required.");
      return;
    }
    if (!contact) {
      setError("Please enter mobile number or email.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const trimmed = contact.trim();
      const isEmail = trimmed.includes("@");
      const isMobile = /^\d{8,15}$/.test(trimmed.replace(/[\s\-()+]/g, ""));
      // Dealers can log in with mobile, email, or username
      const payload = isEmail
        ? { email: trimmed, mobile: "", username: "", password }
        : isMobile
          ? { mobile: trimmed.replace(/[\s\-()+]/g, ""), email: "", username: "", password }
          : { username: trimmed, mobile: "", email: "", password };
      
      const res = await axios.post(`${API_BASE_URL}login`, payload);
      console.log("Login success:", res.data);
          dispatch(addloginData(res?.data))
      // redirect to dashboard or home
      navigate("/panel/orders");
    } catch (err: any) {
      const data = err.response?.data;
      const contentType = String(err.response?.headers?.["content-type"] || "");
      if (!err.response) {
        setError(
          "Cannot reach the API at localhost:8081. Start hthaai_apis (go run main.go) and ensure port 8081 is free."
        );
      } else if (typeof data === "string" && data.includes("<!DOCTYPE html")) {
        setError(
          "Wrong server on port 8081 (not the Thaai API). Stop the other app on that port and restart hthaai_apis."
        );
      } else if (contentType.includes("text/html")) {
        setError(
          "Wrong server on port 8081. Stop the other app using that port and run the Thaai API (hthaai_apis)."
        );
      } else {
        setError(data?.message || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-50 p-4">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-cyan-400/10 rounded-full blur-3xl -z-10 hidden md:block"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-indigo-400/10 to-purple-400/10 rounded-full blur-3xl -z-10 hidden md:block"></div>

      <Card className="w-full max-w-md shadow-2xl border border-slate-200/60 hover:shadow-3xl transition-all duration-300">
        {/* Header with branding */}
        <div className="relative bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 p-8 rounded-t-2xl overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-2 right-2 w-20 h-20 bg-cyan-400/20 rounded-full blur-xl"></div>
            <div className="absolute bottom-2 left-2 w-16 h-16 bg-blue-400/20 rounded-full blur-xl"></div>
          </div>
          
          <div className="relative z-10 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-300 via-blue-400 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/50 hover:shadow-blue-600/70 transition-all hover:scale-110">
                <LogIn className="text-white w-8 h-8" strokeWidth={1.5} />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-white mb-2">Welcome Back</CardTitle>
            <CardDescription className="text-blue-200 text-base">
              Sign in to your Thaai account
            </CardDescription>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-5 pt-8 pb-6">
            {/* Error Alert */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200/60 rounded-xl flex gap-3 animate-shake">
                <div className="w-5 h-5 rounded-full bg-red-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                </div>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Contact Field - Mobile, Email, or Username */}
            <div className="space-y-2.5">
              <Label htmlFor="contact" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Mail size={16} className="text-blue-600" />
                Mobile, Email, or Username
              </Label>
              <div className="relative group">
                <Input
                  id="contact"
                  type="text"
                  value={contact}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContact(e.target.value)}
                  placeholder="Enter mobile, email, or username"
                  required
                  className="pl-4 pr-4 py-3 border-2 border-slate-200/60 rounded-xl bg-white/80 backdrop-blur-sm transition-all focus:border-blue-500 focus:bg-white focus:shadow-lg focus:shadow-blue-500/10 focus:ring-0 placeholder:text-slate-400"
                />
                {contact && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2.5">
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Lock size={16} className="text-blue-600" />
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="pl-4 pr-4 py-3 border-2 border-slate-200/60 rounded-xl bg-white/80 backdrop-blur-sm transition-all focus:border-blue-500 focus:bg-white focus:shadow-lg focus:shadow-blue-500/10 focus:ring-0 placeholder:text-slate-400 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4 pb-8">
            <Button 
              type="submit" 
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 group active:scale-95"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn size={18} className="group-hover:translate-x-1 transition-transform" />
                  Sign In
                </>
              )}
            </Button>

            <button
              type="button"
              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline decoration-blue-200 transition-all p-2 hover:bg-blue-50 rounded-lg w-full"
              onClick={() => navigate("/forgotpassword")}
            >
              Forgot your password?
            </button>
          </CardFooter>
        </form>

        {/* Footer info */}
        <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/50">
          <p className="text-xs text-slate-500 text-center">
            Secure authentication • Your data is protected
          </p>
        </div>
      </Card>
    </div>
  );
}
